import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import translate from '../../../../../../commonComponents/translate';
import Modal from '../../../../../../commonComponents/modal/Modal';
import ModalHeader from './ProductItemModalHeader';
import ProductProp from '../../../../propTypes/ProductProp';
import Gallery from './ProductItemGallery';
import PackProp from '../../../../propTypes/PackProp';
import PlanProp from '../../../../propTypes/PlanProp';
import CartProp from '../../../../propTypes/CartProp';

function ProductItemModal(props) {
  const {
    t,
    onClose,
    product,
    interval,
    currentUserStats,
    selectedPlan,
    onClickRemove,
    onClickAdd,
    cart,
    hasPermissionToChange,
  } = props;

  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const handleEsc = event => {
      if (event.keyCode === 27 && onClose) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc, false);
    return () => {
      document.removeEventListener('keydown', handleEsc, false);
    };
  }, [onClose]);

  const handleChangeQuantity = useCallback(({ type, value }) => {
    setQuantity(prevQuantity => {
      if (type === 'decrement') {
        return prevQuantity > 0 ? prevQuantity - 1 : 0;
      }
      if (type === 'increment') {
        return prevQuantity + 1;
      }
      if (type === 'set') {
        return value > 0 ? value : 1;
      }
      return prevQuantity;
    });
  }, []);

  const handleProductAdd = useCallback(
    p => {
      if (onClickAdd) {
        onClickAdd({ product: p, quantity });
      }
    },
    [onClickAdd, quantity],
  );

  const handleClick = useCallback(() => {
    if (!product) return;
    const { isInCart } = product;
    const cb = isInCart ? onClickRemove : handleProductAdd;
    if (cb) cb(product);
  }, [product, onClickRemove, handleProductAdd]);

  if (!product) return null;
  const { description, images, video } = product;

  return (
    <Modal>
      <div
        className="modal-dialog modal-dialog-centered modal-lg product-item-modal"
        role="document"
      >
        <div className="modal-content">
          <ModalHeader
            t={t}
            onClose={onClose}
            product={product}
            onClick={handleClick}
            handleChangeQuantity={handleChangeQuantity}
            quantity={quantity}
            interval={interval}
            currentUserStats={currentUserStats}
            selectedPlan={selectedPlan}
            cart={cart}
            hasPermissionToChange={hasPermissionToChange}
          />
          <div className="modal-body product-item-modal-content">
            <div style={{ alignSelf: 'flex-start' }}>
              <div className="product-item-modal-resume">
                {t('product-item-modal-resume')}
              </div>
              <div className="product-item-modal-description">
                {description}
              </div>
            </div>
            <Gallery images={images} video={video} />
          </div>
        </div>
      </div>
    </Modal>
  );
}

ProductItemModal.propTypes = {
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  product: PropTypes.oneOfType([
    PropTypes.shape(ProductProp.propType),
    PropTypes.shape(PackProp.propType),
  ]),
  onClickRemove: PropTypes.func,
  onClickAdd: PropTypes.func,
  interval: PropTypes.string.isRequired,
  currentUserStats: PropTypes.shape({
    totalUsers: PropTypes.number,
    totalAdmins: PropTypes.number,
  }).isRequired,
  selectedPlan: PropTypes.shape(PlanProp.propType).isRequired,
  cart: PropTypes.shape(CartProp.propType).isRequired,
  hasPermissionToChange: PropTypes.bool,
};

ProductItemModal.defaultProps = {
  onClose: null,
  product: ProductProp.defaultProp,
  onClickRemove: null,
  onClickAdd: null,
  hasPermissionToChange: true,
};

export default translate(ProductItemModal);
